import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, UserPlus, RefreshCw, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Minus, Link, Unlink,
  Clock, TrendingUp, Settings, Zap
} from 'lucide-react'
import { googleAdsApi, GoogleAdsRfmSegment } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

// Bid modifier display helper
const formatBidModifier = (value: number) => {
  const change = (value - 1) * 100
  return change >= 0 ? `+${change.toFixed(0)}%` : `${change.toFixed(0)}%`
}

// Segment priority colors
const priorityColors: Record<number, string> = {
  1: 'bg-success-500',
  2: 'bg-blue-500',
  3: 'bg-yellow-500',
  4: 'bg-purple-500',
  5: 'bg-orange-500',
  6: 'bg-pink-500',
  7: 'bg-danger-500',
}

// Segment Card Component
function SegmentCard({
  segment,
  onSync,
  onCreateList,
  onUpdateBidModifier,
  isSyncing,
  isCreating
}: {
  segment: GoogleAdsRfmSegment
  onSync: () => void
  onCreateList: () => void
  onUpdateBidModifier: (value: number) => void
  isSyncing: boolean
  isCreating: boolean
}) {
  const [showBidEditor, setShowBidEditor] = useState(false)
  const [newBidModifier, setNewBidModifier] = useState(segment.bid_modifier)

  const bidChange = (segment.bid_modifier - 1) * 100
  const BidIcon = bidChange > 0 ? ArrowUpRight : bidChange < 0 ? ArrowDownRight : Minus

  return (
    <div className={cn(
      "bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden",
      "hover:shadow-lg transition-shadow"
    )}>
      {/* Header with priority color */}
      <div className={cn("h-2", priorityColors[segment.priority] || 'bg-gray-400')} />

      <div className="p-4 space-y-4">
        {/* Segment Name and Status */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              {segment.name}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {segment.description}
            </p>
          </div>
          <div className={cn(
            "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
            segment.is_mapped
              ? "bg-success-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
          )}>
            {segment.is_mapped ? (
              <>
                <Link className="w-3 h-3" />
                Conectado
              </>
            ) : (
              <>
                <Unlink className="w-3 h-3" />
                Sin vincular
              </>
            )}
          </div>
        </div>

        {/* Strategy */}
        <div className="flex items-center gap-2 text-sm">
          <Zap className="w-4 h-4 text-yellow-500" />
          <span className="text-slate-600 dark:text-slate-400">{segment.strategy}</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Miembros</div>
            <div className="text-xl font-bold text-slate-900 dark:text-white">
              {segment.member_count.toLocaleString('es-ES')}
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Modificador puja</div>
            <div className={cn(
              "text-xl font-bold flex items-center gap-1",
              bidChange > 0 ? "text-success-600 dark:text-success-400" :
              bidChange < 0 ? "text-danger-600 dark:text-danger-400" :
              "text-slate-600 dark:text-slate-400"
            )}>
              <BidIcon className="w-4 h-4" />
              {formatBidModifier(segment.bid_modifier)}
            </div>
          </div>
        </div>

        {/* Last Sync */}
        {segment.is_mapped && (
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Clock className="w-3 h-3" />
            {segment.last_sync_at ? (
              <>Ultima sync: {new Date(segment.last_sync_at).toLocaleString('es-ES')}</>
            ) : (
              <>Nunca sincronizado</>
            )}
          </div>
        )}

        {/* Bid Modifier Editor */}
        {showBidEditor && (
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
            <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
              Nuevo modificador de puja
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={newBidModifier}
                onChange={(e) => setNewBidModifier(parseFloat(e.target.value) || 1)}
                step="0.1"
                min="0.1"
                max="10"
                className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
              <button
                onClick={() => {
                  onUpdateBidModifier(newBidModifier)
                  setShowBidEditor(false)
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Guardar
              </button>
              <button
                onClick={() => setShowBidEditor(false)}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600"
              >
                Cancelar
              </button>
            </div>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-2">
              1.0 = sin cambio, 1.4 = +40%, 0.7 = -30%
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
          {segment.is_mapped ? (
            <>
              <button
                onClick={onSync}
                disabled={isSyncing}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={cn("w-4 h-4", isSyncing && "animate-spin")} />
                {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
              </button>
              <button
                onClick={() => setShowBidEditor(!showBidEditor)}
                className="px-3 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                <Settings className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={onCreateList}
              disabled={isCreating}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-success-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50"
            >
              <UserPlus className={cn("w-4 h-4", isCreating && "animate-pulse")} />
              {isCreating ? 'Creando...' : 'Crear audiencia'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function GoogleAdsAudiencesPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [syncingSegment, setSyncingSegment] = useState<string | null>(null)
  const [creatingSegment, setCreatingSegment] = useState<string | null>(null)

  // Queries
  const statusQuery = useQuery({
    queryKey: ['google-ads-status'],
    queryFn: googleAdsApi.getStatus
  })

  const segmentsQuery = useQuery({
    queryKey: ['google-ads-rfm-segments'],
    queryFn: googleAdsApi.getRfmSegments,
    enabled: statusQuery.data?.connected === true
  })

  // Mutations
  const createListMutation = useMutation({
    mutationFn: (segmentName: string) => googleAdsApi.createCustomerMatchList(segmentName),
    onMutate: (segmentName) => setCreatingSegment(segmentName),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-rfm-segments'] })
      toast.success(`Audiencia creada: ${data.segment}`)
    },
    onError: (error: any) => {
      toast.error('Error creando audiencia', error.response?.data?.detail || error.message)
    },
    onSettled: () => setCreatingSegment(null)
  })

  const syncMutation = useMutation({
    mutationFn: (segmentName: string) => googleAdsApi.syncSegment(segmentName),
    onMutate: (segmentName) => setSyncingSegment(segmentName),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-rfm-segments'] })
      toast.success(`Sincronizados ${data.customers_synced} clientes`)
    },
    onError: (error: any) => {
      toast.error('Error sincronizando', error.response?.data?.detail || error.message)
    },
    onSettled: () => setSyncingSegment(null)
  })

  const syncAllMutation = useMutation({
    mutationFn: () => googleAdsApi.syncAllSegments(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-rfm-segments'] })
      toast.success(`Sincronizados ${data.segments_synced} segmentos con ${data.total_customers} clientes`)
    },
    onError: (error: any) => {
      toast.error('Error en sincronizacion masiva', error.response?.data?.detail || error.message)
    }
  })

  const updateBidMutation = useMutation({
    mutationFn: ({ segmentName, bidModifier }: { segmentName: string; bidModifier: number }) =>
      googleAdsApi.updateBidModifier(segmentName, bidModifier),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-rfm-segments'] })
      toast.success(`Modificador actualizado: ${data.display}`)
    },
    onError: (error: any) => {
      toast.error('Error actualizando modificador', error.response?.data?.detail || error.message)
    }
  })

  const isConnected = statusQuery.data?.connected === true
  const segments = segmentsQuery.data?.segments || []
  const summary = segmentsQuery.data

  if (!isConnected) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-yellow-800 dark:text-yellow-200 mb-2">
            Google Ads no conectado
          </h2>
          <p className="text-yellow-700 dark:text-yellow-300">
            Configura la conexion con Google Ads desde el dashboard principal para gestionar audiencias.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Audiencias RFM
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Gestiona las audiencias de remarketing basadas en segmentos RFM de clientes
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['google-ads-rfm-segments'] })}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
          <button
            onClick={() => syncAllMutation.mutate()}
            disabled={syncAllMutation.isPending || segments.filter(s => s.is_mapped).length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <Users className={cn("w-4 h-4", syncAllMutation.isPending && "animate-pulse")} />
            Sincronizar todo
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary.total_segments}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Segmentos totales</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success-100 dark:bg-green-900/30 rounded-lg">
                <Link className="w-5 h-5 text-success-600 dark:text-success-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary.mapped_segments}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Vinculados</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Unlink className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary.unmapped_segments}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Sin vincular</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary.total_members_synced.toLocaleString('es-ES')}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Total miembros</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Segment Cards Grid */}
      {segmentsQuery.isLoading ? (
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Cargando segmentos...</p>
        </div>
      ) : segments.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            No hay segmentos configurados
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            Los segmentos RFM se configuraran automaticamente.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {segments.map((segment) => (
            <SegmentCard
              key={segment.name}
              segment={segment}
              onSync={() => syncMutation.mutate(segment.name)}
              onCreateList={() => createListMutation.mutate(segment.name)}
              onUpdateBidModifier={(value) => updateBidMutation.mutate({
                segmentName: segment.name,
                bidModifier: value
              })}
              isSyncing={syncingSegment === segment.name}
              isCreating={creatingSegment === segment.name}
            />
          ))}
        </div>
      )}

      {/* Info Panel */}
      <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Como funciona el remarketing RFM
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700 dark:text-blue-400">
          <div>
            <h4 className="font-medium mb-2">1. Segmentacion automatica</h4>
            <p>Los clientes se clasifican automaticamente segun su Recencia, Frecuencia y valor Monetario (RFM).</p>
          </div>
          <div>
            <h4 className="font-medium mb-2">2. Audiencias en Google Ads</h4>
            <p>Cada segmento se sincroniza como una lista Customer Match en tu cuenta de Google Ads.</p>
          </div>
          <div>
            <h4 className="font-medium mb-2">3. Modificadores de puja</h4>
            <p>Los modificadores ajustan automaticamente las pujas cuando un cliente del segmento ve un anuncio.</p>
          </div>
          <div>
            <h4 className="font-medium mb-2">4. Estrategias personalizadas</h4>
            <p>Cada segmento tiene una estrategia optima: Champions para cross-sell, At Risk para win-back, etc.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
